import React, { useEffect, useState } from 'react';
import { Plus, Search, Edit, Trash2, Truck, AlertTriangle } from 'lucide-react';
import { Trailer } from '../../../types/saas';
import saasApiService from '../../../services/saasApi';

interface TrailerListProps {
  onEdit: (trailer: Trailer) => void;
  onAdd: () => void;
  refreshTrigger: number;
}

const TrailerList: React.FC<TrailerListProps> = ({ onEdit, onAdd, refreshTrigger }) => {
  const [trailers, setTrailers] = useState<Trailer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchData();
    }, 100);
    return () => clearTimeout(timer);
  }, [refreshTrigger]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await saasApiService.getTrailers();
      let items: any[] = [];
      if (response && response.trailers && Array.isArray(response.trailers)) items = response.trailers;
      else if (response && response.data && response.data.trailers) items = response.data.trailers;
      else if (Array.isArray(response)) items = response;
      else if (response && response.items) items = response.items;
      else if (response && response.data && Array.isArray(response.data)) items = response.data;
      setTrailers(items as Trailer[]);
    } catch (e) {
      console.error('Error fetching trailers:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('¿Eliminar este remolque?')) return;
    try {
      await saasApiService.deleteTrailer(id);
      fetchData();
    } catch (e) {
      console.error('Error deleting trailer:', e);
    }
  };

  const filtered = trailers.filter(t => {
    const q = searchTerm.toLowerCase();
    const matchesSearch =
      (t.plate || '').toLowerCase().includes(q) ||
      (t.brand || '').toLowerCase().includes(q) ||
      (t.model || '').toLowerCase().includes(q) ||
      (t.trailer_type || '').toLowerCase().includes(q);
    const matchesStatus = statusFilter === 'all' || t.status === (statusFilter as any);
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'maintenance': return 'bg-yellow-100 text-yellow-800';
      case 'inactive': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeText = (type: string) => {
    const map: any = { cama_baja: 'Cama Baja', furgon: 'Furgón' };
    return map[type] || (type ? type.charAt(0).toUpperCase() + type.slice(1) : '');
  };

  const isExpiringSoon = (dateStr?: string, days = 30) => {
    if (!dateStr) return false;
    const dt = new Date(dateStr);
    if (isNaN(dt.getTime())) return false;
    const threshold = new Date(Date.now() + days * 24 * 60 * 60 * 1000);
    return dt <= threshold;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestión de Remolques</h1>
          <p className="text-gray-600">Administra carretas, furgones, plataformas, etc.</p>
        </div>
        <button
          onClick={onAdd}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <Plus className="h-4 w-4 mr-2" />
          Agregar Remolque
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Buscar por tipo, placa, marca o modelo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="all">Todos los estados</option>
          <option value="active">Activo</option>
          <option value="maintenance">Mantenimiento</option>
          <option value="inactive">Inactivo</option>
        </select>
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <div className="text-center py-12">
          <Truck className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No hay remolques</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm || statusFilter !== 'all' 
              ? 'No se encontraron remolques con los filtros aplicados.'
              : 'Comienza agregando tu primer remolque.'
            }
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((t) => (
            <div key={t.id} className="bg-white rounded-lg shadow-md border border-gray-200 hover:shadow-lg transition-shadow">
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center">
                    <Truck className="h-8 w-8 text-purple-600 mr-3" />
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {getTypeText(t.trailer_type)} {t.brand ? `- ${t.brand}` : ''}
                      </h3>
                      <p className="text-sm text-gray-500">{t.model || ''} {t.year || ''}</p>
                    </div>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(t.status)}`}>
                    {t.status === 'active' ? 'Activo' : t.status === 'maintenance' ? 'Mantenimiento' : 'Inactivo'}
                  </span>
                </div>
                <div className="space-y-2 mb-4">
                  {t.plate && (
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Placa:</span>
                      <span className="text-sm font-medium text-gray-900">{t.plate}</span>
                    </div>
                  )}
                  {t.capacity_tons != null && (
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Capacidad:</span>
                      <span className="text-sm font-medium text-gray-900">{t.capacity_tons} ton</span>
                    </div>
                  )}
                </div>

                {/* Alertas de vencimiento TUCE/CITV */}
                {(isExpiringSoon((t as any).registration_expiry) || isExpiringSoon((t as any).citv_expiry)) && (
                  <div className="mb-4">
                    {isExpiringSoon((t as any).registration_expiry) && (
                      <div className="flex items-center text-yellow-600 text-xs mb-1">
                        <AlertTriangle className="h-3 w-3 mr-1" /> TUCE vence pronto
                      </div>
                    )}
                    {isExpiringSoon((t as any).citv_expiry) && (
                      <div className="flex items-center text-yellow-600 text-xs">
                        <AlertTriangle className="h-3 w-3 mr-1" /> CITV vence pronto
                      </div>
                    )}
                  </div>
                )}

                <div className="flex justify-end space-x-2 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => onEdit(t)}
                    className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                    title="Editar remolque"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(t.id)}
                    className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                    title="Eliminar remolque"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TrailerList;
